import React, { useState } from 'react';

const AssignReportPopup = ({ 
  reportName,
  reportId,
  users = [],
  onAssignReports,
  onSkip,
  loading
}) => {
  const [selectedUsers, setSelectedUsers] = useState([]);

  // Filter users to show only those with active licenses
  const licensedUsers = users.filter(u => u.hasActiveLicense || u.HasActiveLicense);

  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleAssign = async () => {
    if (selectedUsers.length === 0) {
      alert('Please select at least one user to assign this report to.');
      return;
    }

    await onAssignReports(selectedUsers, [reportId]);
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === licensedUsers.length) {
      // Deselect all
      setSelectedUsers([]);
    } else {
      // Select all licensed users
      setSelectedUsers(licensedUsers.map(u => u.userID || u.UserID));
    }
  };

  const allSelected = selectedUsers.length === licensedUsers.length && licensedUsers.length > 0;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.6)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10002,
      animation: 'fadeIn 0.2s ease-in'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '12px',
        width: '90%',
        maxWidth: '600px',
        maxHeight: '90vh',
        overflow: 'hidden',
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)'
      }}>
        {/* Header */}
        <div style={{
          padding: '1.5rem',
          borderBottom: '1px solid #dee2e6',
          background: '#f8f9fa'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
            <div style={{
              fontSize: '2rem',
              color: '#28a745'
            }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor" viewBox="0 0 16 16" style={{ display: 'block' }}>
                <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>
              </svg>
            </div>
            <div>
              <h3 style={{ margin: '0', color: '#495057', fontSize: '1.3rem' }}>Report Uploaded Successfully!</h3>
              <p style={{ margin: '0.25rem 0 0 0', color: '#6c757d', fontSize: '0.9rem' }}>
                Would you like to assign this report to users now?
              </p>
            </div>
          </div>
        </div>

        {/* Report Info */}
        <div style={{
          padding: '1rem 1.5rem',
          background: '#e3f2fd',
          borderBottom: '1px solid #90caf9'
        }}>
          <p style={{ margin: '0', fontSize: '0.95rem', color: '#0d47a1' }}>
            <strong>Report:</strong> {reportName}
          </p>
        </div>

        {/* Content */}
        <div style={{
          padding: '1.5rem',
          maxHeight: '50vh',
          overflowY: 'auto'
        }}>
          {licensedUsers.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#6c757d' }}>
              <p style={{ margin: '0', fontSize: '1rem', fontWeight: '500' }}>No users with active licenses</p>
              <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem' }}>
                Add users and activate their licenses in the Users tab to assign reports.
              </p>
            </div>
          ) : (
            <>
              {/* Select All */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1rem',
                padding: '0.75rem',
                background: '#f8f9fa',
                borderRadius: '6px'
              }}>
                <span style={{ fontSize: '0.9rem', fontWeight: '600', color: '#495057' }}>
                  Select users to assign ({selectedUsers.length} selected)
                </span>
                <button
                  onClick={handleSelectAll}
                  style={{
                    padding: '0.4rem 0.8rem',
                    border: '1px solid #007bff',
                    borderRadius: '4px',
                    background: allSelected ? '#007bff' : 'white',
                    color: allSelected ? 'white' : '#007bff',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    fontWeight: '500',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {allSelected ? 'Deselect All' : 'Select All'}
                </button>
              </div>

              {/* User List */}
              <div style={{
                border: '1px solid #dee2e6',
                borderRadius: '8px',
                overflow: 'hidden'
              }}>
                {licensedUsers.map((user, index) => {
                  const userId = user.userID || user.UserID;
                  const userName = user.fullName || user.FullName || 'No Name';
                  const userEmail = user.email || user.Email;
                  const isSelected = selectedUsers.includes(userId);
                  const assignedReportsCount = user.assignedReports?.length || user.AssignedReports?.length || 0;

                  return (
                    <div
                      key={userId}
                      onClick={() => toggleUserSelection(userId)}
                      style={{
                        padding: '1rem',
                        background: isSelected ? '#e3f2fd' : (index % 2 === 0 ? '#f8f9fa' : 'white'),
                        border: isSelected ? '2px solid #007bff' : '2px solid transparent',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        borderBottom: index < licensedUsers.length - 1 ? '1px solid #dee2e6' : 'none'
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelected) e.currentTarget.style.background = '#f0f0f0';
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected) e.currentTarget.style.background = index % 2 === 0 ? '#f8f9fa' : 'white';
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {}} // Handled by div click
                          style={{ 
                            marginRight: '0.75rem',
                            width: '18px',
                            height: '18px',
                            cursor: 'pointer'
                          }}
                        />
                        <div style={{ flex: 1 }}>
                          <div style={{ 
                            fontWeight: '600', 
                            marginBottom: '0.25rem',
                            color: '#212529'
                          }}>
                            {userName}
                          </div>
                          <div style={{ 
                            fontSize: '0.85rem', 
                            color: '#6c757d',
                            marginBottom: '0.15rem'
                          }}>
                            {userEmail}
                          </div>
                          <div style={{ 
                            fontSize: '0.75rem', 
                            color: '#868e96'
                          }}>
                            Currently assigned: {assignedReportsCount} report{assignedReportsCount !== 1 ? 's' : ''}
                          </div>
                        </div>
                        {isSelected && (
                          <div style={{ 
                            color: '#007bff', 
                            fontSize: '1.2rem',
                            marginLeft: '0.5rem'
                          }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
                              <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>
                            </svg>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '1rem 1.5rem',
          borderTop: '1px solid #dee2e6',
          background: '#f8f9fa',
          display: 'flex',
          justifyContent: 'space-between',
          gap: '1rem'
        }}>
          <button
            onClick={onSkip}
            disabled={loading}
            style={{
              padding: '0.6rem 1.5rem',
              border: '1px solid #6c757d',
              borderRadius: '6px',
              background: 'white',
              color: '#6c757d',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '0.95rem',
              fontWeight: '500',
              opacity: loading ? 0.6 : 1,
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              if (!loading) {
                e.currentTarget.style.background = '#6c757d';
                e.currentTarget.style.color = 'white';
              }
            }}
            onMouseLeave={(e) => {
              if (!loading) {
                e.currentTarget.style.background = 'white';
                e.currentTarget.style.color = '#6c757d';
              }
            }}
          >
            Skip for Now
          </button>

          <button
            onClick={handleAssign}
            disabled={loading || selectedUsers.length === 0 || licensedUsers.length === 0}
            style={{
              padding: '0.6rem 1.5rem',
              border: 'none',
              borderRadius: '6px',
              background: (loading || selectedUsers.length === 0 || licensedUsers.length === 0) ? '#cccccc' : '#28a745',
              color: 'white',
              cursor: (loading || selectedUsers.length === 0 || licensedUsers.length === 0) ? 'not-allowed' : 'pointer',
              fontSize: '0.95rem',
              fontWeight: '500',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              if (!loading && selectedUsers.length > 0 && licensedUsers.length > 0) {
                e.currentTarget.style.background = '#218838';
              }
            }}
            onMouseLeave={(e) => {
              if (!loading && selectedUsers.length > 0 && licensedUsers.length > 0) {
                e.currentTarget.style.background = '#28a745';
              }
            }}
          >
            {loading ? 'Assigning...' : `Assign to ${selectedUsers.length} User${selectedUsers.length !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssignReportPopup;
